import type { Club, ClubPublicData } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { AvailableIRLSection } from "./AvailableIRLSection";

interface ClubInfoHeaderProps {
  club: Club | ClubPublicData;
  memberCount?: number;
  onMembersClick: () => void;
  onInfoClick: () => void;
  isMember?: boolean;
}

/**
 * ClubInfoHeader - Horizontal header with club image and info side by side.
 *
 * Layout:
 * [256px Club Image] | [Club Name (H3)]
 *                    | [24 members] [Club info button]
 *                    | [Description (2-line truncate)]
 *                    | [Available IRL label + avatars]
 */
export function ClubInfoHeader({
  club,
  memberCount,
  onMembersClick,
  onInfoClick,
  isMember,
}: ClubInfoHeaderProps) {
  const clubImage =
    club.primary_image_medium ||
    club.primary_image_full ||
    club.primary_image_small;

  const displayMemberCount = memberCount ?? club.member_count ?? 0;
  const showMemberCount = club.show_member_count !== false;

  return (
    <div className="px-4 py-6">
      <div className="max-w-[680px] mx-auto flex flex-col md:flex-row gap-8">
        {/* Club Image */}
        <div className="flex-shrink-0">
          {clubImage ? (
            <img
              src={clubImage}
              alt={club.name}
              className="w-full md:w-64 h-48 md:h-64 rounded-3xl object-cover border border-border-tertiary"
            />
          ) : (
            <div className="w-full md:w-64 h-48 md:h-64 rounded-3xl bg-surface-1 border border-border-tertiary flex items-center justify-center">
              <Icon name="globe" size={64} className="text-text-tertiary" />
            </div>
          )}
        </div>

        {/* Info Column */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Club Name */}
          <h1 className="text-h3 text-text-primary mb-3">{club.name}</h1>

          {/* Members + Info buttons row - per Figma: h-32px, px-12, py-8, rounded-[6px] */}
          <div className="flex items-start gap-2 mb-3 flex-wrap">
            <button
              onClick={onMembersClick}
              className="flex-1 min-w-[88px] h-[32px] px-3 rounded-md bg-surface-1 text-[16px] font-medium leading-normal text-text-primary hover:bg-surface-2 transition-colors flex items-center justify-center"
            >
              {showMemberCount
                ? `${displayMemberCount} ${displayMemberCount === 1 ? "member" : "members"}`
                : "Members"}
            </button>

            <button
              onClick={onInfoClick}
              className="flex-1 min-w-[88px] h-[32px] px-3 rounded-md bg-surface-1 text-[16px] font-medium leading-normal text-text-primary hover:bg-surface-2 transition-colors flex items-center justify-center"
            >
              Club info
            </button>
          </div>

          {/* Description (2-line truncate) */}
          {club.description && (
            <p className="text-body-2 text-text-secondary line-clamp-2 mb-4">
              {club.description}
            </p>
          )}

          {/* Available IRL Section */}
          {club.id && isMember && (
            <AvailableIRLSection clubId={club.id} onMembersClick={onMembersClick} />
          )}
        </div>
      </div>
    </div>
  );
}
